import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { HumanizePipe } from '@app/core/pipes/humanize.pipe';
import { ModalService } from '@app/core/services/modal/modal.service';
import { ToolbarService } from '@app/core/services/toolbar/toolbar.service';
import { UiConfigVisibilityEnum } from '@app/generated/graphql';
import { Angulartics2 } from 'angulartics2';
import { ChartType } from 'chart.js';
import {
  PivotTableHeader,
  PivotTableValue,
} from '../../raw-pivot-table/raw-pivot-table.component';
import { Ag1ButtonComponent } from '../../ag1-button/ag1-button.component';
import { NgSelectComponent, NgOptionComponent } from '@ng-select/ng-select';
import { FormsModule } from '@angular/forms';
import { ModalHostDirective } from '../../../directives/modal-host.directive';

export interface PivotReport {
  id?: number;
  configType: string;
  name: string;
  rows: PivotTableHeader[];
  cols: PivotTableHeader[];
  values: PivotTableValue[];
  visibility: UiConfigVisibilityEnum;
  renderer: 'table' | ChartType;
  readonly: boolean;
  dirty: boolean;
}

@Component({
  selector: 'ag-toolbar',
  templateUrl: './toolbar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    Ag1ButtonComponent,
    NgSelectComponent,
    FormsModule,
    NgOptionComponent,
    ModalHostDirective,
  ],
})
export class ToolbarComponent implements OnInit, OnChanges {
  private modalService = inject(ModalService);
  private angulartics2 = inject(Angulartics2);
  private humanizePipe = inject(HumanizePipe);
  private toolbarService = inject(ToolbarService);

  @Input()
  configType: string;

  @Input()
  showCreate: boolean;

  @Input()
  showSave: boolean;

  @Input()
  showDuplicate: boolean;

  @Input()
  showRename: boolean;

  @Input()
  showDelete: boolean;

  @Input()
  canSaveAsPublic: boolean;

  @Input()
  reports: PivotReport[] = [];

  @Input()
  selectedReport: PivotReport = this.reports[0];

  @Input()
  cols: PivotTableHeader[] = [];
  @Input()
  rows: PivotTableHeader[] = [];
  @Input()
  values: PivotTableValue[] = [];
  @Input()
  data: any[] = [];

  @Output()
  selectedReportChange: EventEmitter<PivotReport> = new EventEmitter();

  @Output()
  reportSaved: EventEmitter<PivotReport> = new EventEmitter();

  @Output()
  reportDeleted: EventEmitter<PivotReport> = new EventEmitter();

  @Output()
  exportClicked: EventEmitter<string> = new EventEmitter();

  constructor() {}

  ngOnInit(): void {
    this.loadConfigs();
  }

  loadConfigs() {
    this.toolbarService.load(this.configType).subscribe((res) => {
      this.reports = res;

      if (this.reports.length > 0) {
        this.selectedReport = this.reports[0];
        this.selectedReportChange.emit(this.selectedReport);
      }
    });
    return;
  }

  ngOnChanges(changes: SimpleChanges) {
    for (const propName in changes) {
      if (
        this.selectedReport &&
        (propName === 'rows' ||
          propName === 'cols' ||
          propName === 'values' ||
          propName === 'renderer')
      ) {
        if (this.selectedReport[propName] != changes[propName].currentValue) {
          this.selectedReport[propName] = changes[propName].currentValue;
          this.selectedReport.dirty = true;
        }
      }
    }
  }

  handleReportChange(event: PivotReport) {
    this.selectedReportChange.emit(event);
  }

  handleVisibiltyChange(_event: UiConfigVisibilityEnum) {
    this.selectedReport.dirty = true;
  }

  handleRendererChange(_event) {
    this.selectedReport.dirty = true;

    this.selectedReportChange.emit(this.selectedReport);
  }

  handleCreateClick() {
    const modalInstance = this.modalService.showInput({
      title: 'Create new report',
      field: {
        inputType: 'text',
        inputLabel: 'Report name',
        placeholder: 'enter report name',
        required: true,
      },
      confirmBtnClass: 'is-link',
      confirmBtnText: 'Create report',
      onConfirm: (reportName: string) => {
        const newReport: PivotReport = {
          name: reportName,
          configType: this.configType,
          cols: [],
          rows: [],
          values: [],
          readonly: false,
          renderer: 'table',
          visibility: UiConfigVisibilityEnum.Private,
          dirty: true,
        };
        this.selectedReport = newReport;
        this.selectedReportChange.emit(this.selectedReport);
        this.reports = [...this.reports, newReport];

        modalInstance.hide();
      },
    });
  }

  handleDeleteClick() {
    const modalInstance = this.modalService.showConfirm({
      content: `This will delete the ${this.selectedReport.name} report, are you sure?`,
      title: 'Delete report?',
      confirmBtnText: 'Delete report',
      confirmBtnClass: 'is-danger',
      onConfirm: () => {
        this.trackAnalytics('Delete');

        if (this.selectedReport.id) {
          modalInstance.isLoading = true;

          this.toolbarService.delete(this.selectedReport).subscribe(
            (_res) => {
              this.removeReport();
              modalInstance.isLoading = false;

              modalInstance.hide();
            },
            (err) => {
              console.error(err);
              modalInstance.isLoading = false;
              modalInstance.hide();
            },
          );
        } else {
          this.removeReport();
          modalInstance.hide();
        }
      },
    });
  }

  removeReport() {
    this.reports = this.reports.filter((r) => r != this.selectedReport);
    this.selectedReport = this.reports[0];
    this.selectedReportChange.emit(this.selectedReport);
  }

  createReport() {
    this.toolbarService.create(this.selectedReport).subscribe((res) => {
      this.selectedReport = res;
    });
  }

  updateReport() {
    this.toolbarService.update(this.selectedReport).subscribe((res) => {
      this.selectedReport = res;
    });
  }

  handleSaveClick() {
    if (this.selectedReport.id) {
      this.trackAnalytics('Update');
      this.updateReport();
    } else {
      this.trackAnalytics('Create');
      this.createReport();
    }
  }

  handleDuplicateClick() {
    const modalInstance = this.modalService.showInput({
      title: 'Create Copy',
      field: {
        inputType: 'text',
        inputLabel: 'New report name',
        placeholder: 'enter report name',
        required: true,
      },
      confirmBtnClass: 'is-link',
      confirmBtnText: 'Create Copy',
      onConfirm: (reportName: string) => {
        this.trackAnalytics('Duplicate', {
          new_name: reportName,
        });

        const newReport: PivotReport = {
          ...this.selectedReport,
          id: undefined,
          dirty: true,
          name: reportName,
          visibility: UiConfigVisibilityEnum.Private,
        };

        // select the new report
        this.selectedReport = newReport;

        // add it to the list of available reports
        this.reports = [...this.reports, newReport];

        modalInstance.hide();
      },
    });
  }

  handleRenameClick() {
    const modalInstance = this.modalService.showInput({
      title: 'Rename Report',
      field: {
        inputType: 'text',
        inputLabel: 'Report name',
        placeholder: 'enter report name',
        defaultValue: this.selectedReport.name,
        required: true,
      },
      confirmBtnClass: 'is-link',
      confirmBtnText: 'Rename report',
      onConfirm: (reportName: string) => {
        this.trackAnalytics('Rename', {
          new_name: reportName,
        });

        this.selectedReport.name = reportName;
        this.selectedReport.dirty = true;

        // save it on the backend
        this.updateReport();

        modalInstance.hide();
      },
    });
  }

  handleExportClick() {
    this.exportClicked.emit(this.selectedReport.name);

    this.trackAnalytics('Export');
  }

  trackAnalytics(
    action: 'Create' | 'Update' | 'Delete' | 'Rename' | 'Duplicate' | 'Export',
    extraProperties: object = null,
  ) {
    if (!this.selectedReport) return;

    const eventType = this.humanizePipe.transform(this.configType);

    this.angulartics2.eventTrack.next({
      action: `${eventType}: ${action} Report`,
      properties: {
        report_name: this.selectedReport.name,
        report_id: this.selectedReport.id,
        ...extraProperties,
      },
    });
  }
}
